import { useState } from 'react'
import { CalendarClock, FileWarning } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { AssetStrip } from './AssetStrip'
import { UploadButton } from './UploadButton'
import { useAttachDesignAsset, useUpdateDesignRequest } from '../../hooks/backend/marketing'
import { writeErrorMessage } from '../../utils/writeError'
import {
  designAttachmentRights,
  shortDate,
  type ContentRow,
  type DesignRequestRow,
} from '../../utils/marketing'

const STATUSES = ['Requested', 'In progress', 'Delivered', 'Cancelled'] as const

/**
 * The design queue.
 *
 * Everyone sees the whole queue, not only their own requests. A designer with a
 * private list is a designer nobody can plan around, and the most common
 * question here is "is mine behind five other things", which a filtered view
 * cannot answer.
 *
 * Delivering needs a file attached. The database refuses Delivered with nothing
 * on it, so the Delivered option is offered and the refusal explains itself
 * rather than the option being hidden, which would leave the designer wondering
 * why the status will not move.
 */
export function DesignRequestBoard({
  rows,
  contentItems,
  signedInEmail,
  canAttachToContent,
  isDesigner,
  onChanged,
  openId,
  onOpen,
}: {
  rows: DesignRequestRow[]
  contentItems: ContentRow[]
  signedInEmail: string | null
  // canEditMarketing, which already folds in admin. Named for what it is used
  // for at the call site it came from.
  canAttachToContent: boolean
  isDesigner: boolean
  onChanged: () => void
  // Which row is expanded, held by the page rather than here, so a notification
  // can open one directly. Migration 0152. The same shape ContentBoard already
  // uses for selectedId and onOpen, so the two boards read the same way.
  openId: string | null
  onOpen: (id: string | null) => void
}) {
  const update = useUpdateDesignRequest()
  const attach = useAttachDesignAsset()
  const [busyId, setBusyId] = useState<string | null>(null)

  async function setStatus(row: DesignRequestRow, status: string) {
    setBusyId(row.id)
    try {
      await update.trigger({ id: row.id, patch: { status } }).result
      toast.success(`Moved to ${status.toLowerCase()}`)
      onChanged()
    } catch (err) {
      toast.error('That did not move', { description: writeErrorMessage(err) })
    } finally {
      setBusyId(null)
    }
  }

  async function useAsContent(assetId: string, contentItemId: string) {
    setBusyId(assetId)
    try {
      await attach.trigger({ asset_id: assetId, content_item_id: contentItemId }).result
      toast.success('Attached to that content', {
        description: 'The design request keeps a copy of it too.',
      })
      onChanged()
    } catch (err) {
      toast.error('That was not attached', { description: writeErrorMessage(err) })
    } finally {
      setBusyId(null)
    }
  }

  if (rows.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">
        Nothing in the design queue. Use Ask for a design in the menu on the left.
      </p>
    )
  }

  return (
    <ul className="divide-y divide-border">
      {rows.map((row) => {
        const mine =
          signedInEmail !== null &&
          (row.requested_by?.toLowerCase() === signedInEmail.toLowerCase() ||
            row.assigned_to?.toLowerCase() === signedInEmail.toLowerCase())
        const expanded = openId === row.id
        // The designer it is with, the person who asked, and an admin. The
        // database enforces the same list, so a control offered here that would
        // be refused there is a control that should not be offered.
        const canMove = mine
        // WHO MAY ATTACH WHAT IS NOT THE SAME QUESTION AS WHO MAY MOVE THE
        // STATUS, and until migration 0118 this board treated them as one: the
        // Deliver a file button was shown to anyone whose request it was, and the
        // Worker then refused every one of them who had no marketing access. The
        // rules now live in one function that mirrors the database, so the button
        // that appears is the button that works.
        const rights = designAttachmentRights(row, {
          email: signedInEmail,
          canEditMarketing: canAttachToContent,
          isDesigner,
        })
        // Draft and Changes requested only. Approved and Published content
        // refuses new files, and an option that always fails is worse than no
        // option.
        const attachable = contentItems.filter(
          (c) => c.state === 'Draft' || c.state === 'Changes requested',
        )

        return (
          <li key={row.id} className="px-4 py-3">
            <button
              type="button"
              onClick={() => onOpen(expanded ? null : row.id)}
              className="flex w-full flex-col gap-1.5 text-left"
            >
              <div className="flex min-w-0 items-start gap-2">
                <span className="mt-0.5 shrink-0 text-[11px] tabular-nums text-muted-foreground">
                  #{row.ref}
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
                  {row.title}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip
                  tone={
                    row.status === 'Delivered'
                      ? 'green'
                      : row.status === 'Cancelled'
                        ? 'neutral'
                        : row.status === 'In progress'
                          ? 'brand'
                          : 'amber'
                  }
                >
                  {row.status}
                </Chip>
                <span className="text-[11px] text-muted-foreground">
                  for {row.assigned_to_name ?? 'the designer'}
                </span>
                {row.priority && row.priority !== 'Medium' && (
                  <Chip tone={row.priority === 'High' ? 'red' : 'neutral'}>{row.priority}</Chip>
                )}
                {row.overdue && (
                  <Chip tone="red" icon={<CalendarClock className="h-3 w-3" />}>
                    past {shortDate(row.needed_by)}
                  </Chip>
                )}
                {row.delivered_with_no_file && (
                  <Chip tone="amber" icon={<FileWarning className="h-3 w-3" />}>
                    delivered with no file
                  </Chip>
                )}
                {row.content_item_title && (
                  <span className="text-[11px] text-muted-foreground">
                    for {row.content_item_title}
                  </span>
                )}
                <span className="ml-auto text-[11px] text-muted-foreground">
                  asked {shortDate(row.requested_at)}
                  {row.needed_by ? `, needed ${shortDate(row.needed_by)}` : ''}
                </span>
              </div>
            </button>

            {expanded && (
              <div className="mt-3 space-y-3 rounded-md border border-border bg-muted/30 p-3">
                <p className="whitespace-pre-wrap text-xs text-foreground">{row.brief}</p>
                {(row.dimensions || row.format_label || row.channel_label) && (
                  <p className="text-[11px] text-muted-foreground">
                    {[row.channel_label, row.format_label, row.dimensions]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
                {row.reference_urls && row.reference_urls.length > 0 && (
                  <ul className="space-y-0.5">
                    {row.reference_urls.map((u) => (
                      <li key={u}>
                        <a
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-primary underline underline-offset-2"
                        >
                          {u}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}

                {/* What the person asking sent in. Reads ABOVE the artwork,
                    because that is the order the work happens in and because a
                    designer opening a request wants the brief and its pictures
                    before the delivery slot. Only shown when there is something
                    in it, or when this person can add to it. */}
                {((row.reference_assets?.length ?? 0) > 0 || rights.canAttachReference) && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-foreground">Sent in with the request</p>
                      {rights.canAttachReference && (
                        <UploadButton
                          designRequestId={row.id}
                          kind="Reference"
                          label="Attach a file"
                          onUploaded={onChanged}
                        />
                      )}
                    </div>
                    <AssetStrip
                      assets={row.reference_assets ?? []}
                      canRemove={rights.canRemoveReference}
                      onChanged={onChanged}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-foreground">Artwork</p>
                    {rights.canDeliverArtwork && (
                      <UploadButton
                        designRequestId={row.id}
                        kind="Artwork"
                        label="Deliver a file"
                        onUploaded={onChanged}
                      />
                    )}
                  </div>
                  <AssetStrip
                    assets={row.assets ?? []}
                    canRemove={rights.canRemoveArtwork}
                    onChanged={onChanged}
                  />

                  {canAttachToContent &&
                    row.assets?.length > 0 &&
                    attachable.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">
                          Use the newest file as content:
                        </span>
                        <NativeSelect
                          className="h-8 w-56 text-xs"
                          defaultValue=""
                          disabled={busyId !== null}
                          onChange={(e) => {
                            if (!e.target.value) return
                            void useAsContent(row.assets[0].id, e.target.value)
                            e.target.value = ''
                          }}
                        >
                          <option value="">Choose the content</option>
                          {attachable.map((c) => (
                            <option key={c.id} value={c.id}>
                              #{c.ref} {c.title}
                            </option>
                          ))}
                        </NativeSelect>
                      </div>
                    )}
                </div>

                {canMove && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">Status</span>
                    <NativeSelect
                      className="h-8 w-40 text-xs"
                      value={row.status}
                      disabled={busyId === row.id}
                      onChange={(e) => void setStatus(row, e.target.value)}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </NativeSelect>
                    {busyId === row.id && <Spinner className="h-3.5 w-3.5" />}
                    {row.asset_count === 0 && (
                      <span className="text-[11px] text-muted-foreground">
                        {(row.reference_count ?? 0) > 0
                          ? 'Delivered needs the artwork attached. What is on here was sent in with the request.'
                          : 'Delivered needs a file attached first.'}
                      </span>
                    )}
                  </div>
                )}
                {!canMove && (
                  <p className="text-[11px] text-muted-foreground">
                    Only the person who asked, the designer it is with, or an admin can change this.
                  </p>
                )}

                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => onOpen(null)}
                >
                  Close
                </Button>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
